import * as importTypesActions from './import-types.actions';
import { importTypesInitialState } from './import-types.init-state';
import { Action } from '../../shared/interfaces/shared';
import { ImportTypesState } from '../interfaces/state/import-types-state';
import { ImportLines } from '../interfaces/import-lines';
import { ImportType } from '../interfaces/import-type';

/**
 * Remplace la ligne du type d'import dans un nouveau tableau.
 * Muter l'objet en place laisserait la référence de state.importTypes inchangée, et le
 * distinctUntilChanged de import-types.service bloquerait l'émission : la grille ne se
 * redessinerait qu'au rechargement de la page.
 */
const patchImportType = (state: ImportTypesState, importType: ImportType): ImportTypesState => ({
    ...state,
    importTypes: state.importTypes.map(cachedImportType => cachedImportType.id === importType.id
        ? {
            ...cachedImportType,
            totalFeatures: importType.totalFeatures,
            updatedBy: importType.updatedBy,
            updatedAt: importType.updatedAt,
        }
        : cachedImportType),
});

export const importTypesReducer = (state: ImportTypesState = importTypesInitialState, action: Action<any>): ImportTypesState => {
    switch (action.type) {
        case importTypesActions.IMPORTING: {
            return {
                ...state,
                importingFile: action.payload as boolean,
            };
        }

        case importTypesActions.GET_IMPORT_TYPES_OK: {
            return {
                ...state,
                importTypes: action.payload as ImportType[],
            };
        }

        case importTypesActions.SET_ADD_IMPORT_TYPE_MODAL: {
            return {
                ...state,
                addImportTypeModal: action.payload as boolean,
                // Sans ce reset, l'erreur de la tentative précédente se rafficherait
                // à l'ouverture suivante du formulaire
                addImportTypeError: '',
            };
        }

        case importTypesActions.CREATE_IMPORT_TYPE_OK: {
            return {
                ...state,
                importTypes: [...state.importTypes, action.payload as ImportType],
            };
        }

        case importTypesActions.CREATE_IMPORT_TYPE_FAIL: {
            return {
                ...state,
                addImportTypeError: action.payload as string,
            };
        }

        case importTypesActions.CLEAR_IMPORT_TYPE_OK: {
            return patchImportType(state, action.payload as ImportType);
        }

        case importTypesActions.CLEAR_IMPORT_TYPE_FAIL: {
            return {
                ...state,
                clearImportTypeError: action.payload as string,
            };
        }

        case importTypesActions.REFRESH_TOTALS_FAIL: {
            return {
                ...state,
                refreshTotalsError: action.payload as string,
            };
        }

        case importTypesActions.UPLOAD_GEOJSON_FILE_OK: {
            return patchImportType(state, action.payload as ImportType);
        }

        case importTypesActions.UPLOAD_GEOJSON_FILE_FAIL: {
            return {
                ...state,
                uploadGeoJsonFileError: action.payload as string,
            };
        }

        // L'indicateur est piloté ici et pas dans l'epic : celui-ci abandonne les requêtes périmées
        // (switchMap) sans que leur suite s'exécute, un setLoading(false) posé dans l'epic ne serait
        // donc pas garanti. Seule la dernière requête émise éteint l'indicateur.
        case importTypesActions.GET_IMPORT_LINES_REQ: {
            return {
                ...state,
                loadingImportLines: true,
            };
        }

        case importTypesActions.GET_IMPORT_LINES_OK: {
            return {
                ...state,
                importLines: action.payload as ImportLines,
                loadingImportLines: false,
            };
        }

        case importTypesActions.GET_IMPORT_LINES_FAIL: {
            return {
                ...state,
                getImportLinesError: action.payload as string,
                loadingImportLines: false,
            };
        }

        case importTypesActions.CLEAR_IMPORT_LINES: {
            return {
                ...state,
                importLines: null,
                loadingImportLines: false,
            };
        }

        default: {
            return state;
        }
    }
};
